/**
 * Settings panel — partner / Tray-support ACCESS (ADR-0081 S3, decision D5).
 *
 * The panel exists because the decision is the CUSTOMER's. Control records who their
 * reseller is; whether that reseller can actually open a session here is decided by the
 * two controls on this page, on this instance. Changing reseller is done by editing the
 * code — the old partner simply stops matching, with no ticket to us and no revocation
 * pushed from anywhere.
 *
 * Two things the UI must get right, both about honesty rather than looks:
 *
 * 1. **Switching consent off ends sessions in progress**, and the panel SAYS so before
 *    you flip it. A switch the user thinks only affects future logins would be a nasty
 *    surprise in the one direction where surprises are unacceptable.
 * 2. **The visit history is shown next to the switch**, not hidden elsewhere. Consent is
 *    only meaningful if you can see afterwards that someone came in, when, and why the
 *    visit ended.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { type PartnerSession, partnerAccessApi } from "@/lib/api-settings";
import { usePermissions } from "@/lib/permissions";

/** Why a visit ended, in the customer's words. `idle` and `logout` are different facts
 *  (walked away vs left deliberately) and the panel keeps them distinct. */
const END_REASON: Record<string, string> = {
  idle: "Inattività",
  expired: "Scaduta",
  logout: "Uscita",
  consent_revoked: "Accesso revocato",
};

function SessionRow({ row }: { row: PartnerSession }) {
  const started = new Date(row.started_at).toLocaleString();
  const ended = row.ended_at ? new Date(row.ended_at).toLocaleString() : null;
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border py-2 text-sm last:border-b-0">
      <div className="min-w-0">
        <span className="font-medium text-foreground">{row.display_name}</span>
        {row.subject === "tray_support" && (
          <span className="ml-2 rounded-full bg-elevated px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            Tray
          </span>
        )}
        <div className="truncate text-xs text-muted-foreground">
          Autorizzato da {row.issued_by || "—"}
        </div>
      </div>
      <div className="shrink-0 text-right text-xs text-muted-foreground">
        <div className="tabular-nums">{started}</div>
        <div>
          {row.active ? (
            <span className="font-medium text-success">In corso</span>
          ) : (
            <>
              {ended} · {END_REASON[row.end_reason ?? ""] ?? row.end_reason ?? "—"}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function PartnerAccessSection() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const qc = useQueryClient();
  const canWrite = can("settings", "write");

  const accessQ = useQuery({ queryKey: ["partner-access"], queryFn: partnerAccessApi.get });
  const sessionsQ = useQuery({
    queryKey: ["partner-access", "sessions"],
    queryFn: partnerAccessApi.sessions,
  });

  const [code, setCode] = React.useState("");
  const [dirty, setDirty] = React.useState(false);
  // The stored value seeds the field ONCE per load; typing takes over from there, so a
  // background refetch can never yank a half-typed code out from under the operator.
  React.useEffect(() => {
    if (accessQ.data && !dirty) setCode(accessQ.data.code);
  }, [accessQ.data, dirty]);

  const save = useMutation({
    mutationFn: partnerAccessApi.put,
    onSuccess: () => {
      setDirty(false);
      void qc.invalidateQueries({ queryKey: ["partner-access"] });
    },
  });

  if (!accessQ.data) {
    return <p className="text-sm text-muted-foreground">{t("common.loading", "Caricamento…")}</p>;
  }
  const access = accessQ.data;
  const live = (sessionsQ.data ?? []).filter((s) => s.active).length;

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-border bg-surface p-5">
        <h3 className="text-sm font-semibold text-foreground">Accesso del partner</h3>
        <p className="mb-4 mt-0.5 text-xs text-muted-foreground">
          Decidi tu se il tuo rivenditore (o l'assistenza Tray) può aprire una sessione su questa
          istanza. Ogni accesso è registrato qui sotto.
        </p>

        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={access.enabled}
            disabled={!canWrite || save.isPending}
            onChange={(e) => save.mutate({ enabled: e.target.checked })}
          />
          <span className="text-sm">
            <span className="font-medium text-foreground">Consenti l'accesso</span>
            <span className="block text-xs text-muted-foreground">
              {access.enabled && live > 0
                ? `Disattivando, ${live === 1 ? "la sessione in corso verrà chiusa" : `le ${live} sessioni in corso verranno chiuse`} subito.`
                : "Senza consenso nessuno entra, anche con un'autorizzazione valida."}
            </span>
          </span>
        </label>

        <div className="mt-4 space-y-1">
          <label className="text-xs font-medium text-muted-foreground" htmlFor="partner-code">
            Codice rivenditore
          </label>
          <div className="flex items-start gap-2">
            <Input
              id="partner-code"
              value={code}
              disabled={!canWrite}
              placeholder="es. acme"
              onChange={(e) => {
                setCode(e.target.value);
                setDirty(true);
              }}
            />
            <Button
              disabled={!canWrite || !dirty || save.isPending}
              onClick={() => save.mutate({ code })}
            >
              {t("common.save", "Salva")}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {access.code_from_install
              ? "Impostato dall'installazione. Cambiandolo qui il vecchio rivenditore non entra più."
              : "Cambia rivenditore inserendo il suo codice: chi aveva il precedente non entra più."}
          </p>
          {save.isError && (
            <p className="text-xs text-danger">
              {(save.error as Error)?.message ?? t("common.error", "Errore")}
            </p>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-border bg-surface p-5">
        <h3 className="text-sm font-semibold text-foreground">Accessi registrati</h3>
        <p className="mb-3 mt-0.5 text-xs text-muted-foreground">
          Chi è entrato, quando, e come è finita la sessione. Le singole azioni sono nel registro di
          audit, sotto lo stesso nome.
        </p>
        {(sessionsQ.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">Nessun accesso finora.</p>
        ) : (
          <div>
            {(sessionsQ.data ?? []).map((row) => (
              <SessionRow key={row.id} row={row} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
